/**
 * Right-side panel chrome — owns the active surface heading, compact surface
 * switcher, and global close button so Browser / Terminal / Design / Diff /
 * Plan feel like one cohesive view instead of independent overlays.
 *
 * The sidebar resize / `SidebarProvider` wrapping lives in the route file;
 * this component is a pure chrome+slot, with the tab strip itself in
 * `rightSidebarSurfaceTabStrip`. That keeps the resize behaviour unchanged
 * while consolidating the in-sidebar UX.
 */
import { PanelRightIcon } from "lucide-react";
import { RIGHT_SIDEBAR_SHELL_HEADER_CLASS } from "./RightSidebarShell.classes";
import type { ReactNode } from "react";
import { useMemo } from "react";

import type { RightPanelKind, RightPanelSurface } from "../rightPanelSurface";
import { isElectron } from "../env";
import { cn } from "~/lib/utils";

import { RightPanelSurfaceLauncher } from "./RightPanelSurfaceLauncher";
import { buildAddableRightPanelKinds, buildRightPanelSurfaceTabs } from "./rightPanelSurfaceTabs";
import {
  AddSurfaceMenu,
  SurfaceTabStrip,
  type RightPanelPreviewAudio,
} from "./rightSidebarSurfaceTabStrip";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

export interface RightSidebarShellProps {
  /** Open surfaces, in strip order. Several may share a kind. */
  readonly surfaces: readonly RightPanelSurface[];
  readonly activeSurfaceId: string | null;
  readonly isServerThread: boolean;
  readonly shortcuts?: Partial<Record<RightPanelKind, string | null>> | undefined;
  /** Display names for surfaces that cannot name themselves, by surface id. */
  readonly resourceLabels?: Readonly<Record<string, string>> | undefined;
  /** Activate an open surface. Takes a surface id, so two files are two tabs. */
  readonly onSelectSurface: (surfaceId: string) => void;
  readonly onCloseSurface: (surfaceId: string) => void;
  /** Open a new surface of this kind from the plus menu. */
  readonly onAddSurface: (kind: RightPanelKind) => void;
  /** Only show the add affordance when this panel can complete the action. */
  readonly showAddSurface?: boolean;
  readonly onClose: () => void;
  /**
   * Name a tab in place; a blank label clears the name back to the derived
   * one. Absent where nothing can be renamed, which also removes the menu
   * item: an inert Rename is worse than none.
   */
  readonly onRenameSurface?: (surfaceId: string, label: string) => void;
  /** Mute controls for preview tabs; absent where there is nothing to mute. */
  readonly previewAudio?: RightPanelPreviewAudio;
  /**
   * Controls that belong to the panel's own strip rather than the chat header.
   * t3code groups maximize with the panel toggles at the top-right of whichever
   * container owns that corner; once the panel is open, that is the panel.
   */
  readonly layoutControls?: ReactNode;
  readonly children: ReactNode;
}

/**
 * The right panel's chrome: the surface tab strip with its add menu and the
 * panel-level controls, wrapping whatever pane the active surface renders as
 * children.
 */
export function RightSidebarShell(props: RightSidebarShellProps) {
  const {
    surfaces,
    activeSurfaceId,
    isServerThread,
    shortcuts,
    resourceLabels,
    onSelectSurface,
    onCloseSurface,
    onAddSurface,
    showAddSurface = true,
    onClose,
    onRenameSurface,
    previewAudio,
    layoutControls,
    children,
  } = props;
  const tabs = useMemo(
    () =>
      buildRightPanelSurfaceTabs({
        surfaces,
        activeSurfaceId,
        isServerThread,
        ...(shortcuts !== undefined ? { shortcuts } : {}),
        ...(resourceLabels !== undefined ? { resourceLabels } : {}),
      }),
    [activeSurfaceId, isServerThread, resourceLabels, shortcuts, surfaces],
  );
  const addableKinds = useMemo(
    () =>
      buildAddableRightPanelKinds({
        surfaces,
        isServerThread,
        ...(shortcuts !== undefined ? { shortcuts } : {}),
      }),
    [isServerThread, shortcuts, surfaces],
  );
  const activeTab = tabs.find((tab) => tab.active) ?? null;
  const vm = { visibleTabs: tabs, activeTab, addableTabs: addableKinds, availableTabs: tabs };

  return (
    <div className="flex h-full min-w-0 flex-col bg-card text-foreground">
      {/*
        Top bar height + horizontal padding mirror `ChatHeader`'s wrapper in
        `ChatView.tsx` so the view heading on the right is vertically
        flush with the thread title bar on the left. Keep the two in sync if
        either side changes its sizing.
      */}
      <div
        className={cn(
          RIGHT_SIDEBAR_SHELL_HEADER_CLASS,
          isElectron ? "sm:h-[52px] wco:h-[env(titlebar-area-height)]" : "sm:h-[52px]",
        )}
        data-testid="right-sidebar-shell-header"
      >
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <SurfaceTabStrip
            tabs={vm.visibleTabs}
            onSelect={onSelectSurface}
            onClose={onCloseSurface}
            {...(onRenameSurface !== undefined ? { onRenameSurface } : {})}
            {...(previewAudio !== undefined ? { previewAudio } : {})}
          />
          {showAddSurface ? (
            <AddSurfaceMenu kinds={vm.addableTabs} onSelect={onAddSurface} />
          ) : null}
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-0.5">
          {layoutControls}
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label="Close right sidebar"
                  onClick={onClose}
                  data-testid="right-sidebar-shell-close"
                  className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:size-6 sm:rounded-md"
                >
                  <PanelRightIcon aria-hidden="true" className="size-4" />
                </button>
              }
            />
            <TooltipPopup>Hide sidebar</TooltipPopup>
          </Tooltip>
        </div>
      </div>
      <div
        className="flex min-h-0 min-w-0 flex-1 flex-col"
        role="tabpanel"
        aria-label={vm.activeTab ? `${vm.activeTab.label} panel` : "Right sidebar"}
      >
        {vm.activeTab ? (
          children
        ) : showAddSurface ? (
          <RightPanelSurfaceLauncher kinds={vm.addableTabs} onSelect={onAddSurface} />
        ) : null}
      </div>
    </div>
  );
}
